import {
	Runnit,
	RunnitMember,
	RUNNIT_TYPE,
	RUNNITS_ACCESS_LEVEL,
	RunnitsAccessLevel,
	TeamMember,
} from '@run-diffusion/shared';

/**
 * The bulk Access Settings dialog's "leave each board's access level as it is". Not a level a
 * board can have: choosing it sends no accessLevel, so an owner change can go out on its own.
 */
export const KEEP_ACCESS_LEVEL = 'KEEP' as const;
export type AccessLevelChoice = RunnitsAccessLevel | typeof KEEP_ACCESS_LEVEL;

/** One card in the Access Settings dialog's "Who can access" choice. */
export interface AccessLevelOption {
	value: AccessLevelChoice
	label: string
	description: string
	icon: string
	// The accent the card wears: the pink or blue the Boards table uses for the level
	color: string
	disabled?: boolean
}

/** What `manageRunnitBoardAccessSettings` takes for one board. */
export interface ManageAccessPayload {
	runnitId: string
	accessLevel?: RunnitsAccessLevel
	ownerUserId?: string
	addedUserIds?: string[]
	removedUserIds?: string[]
	setUserIds?: string[]
}

export interface OwnerChangeContext {
	/** The team the boards come from: the Vuex `team` in team mode, null in personal mode. */
	teamId: string | null
	isAdmin: boolean
	hasManageAllGenerationsPermission: boolean
}

/**
 * Whether the caller may hand this board to someone else. The same rule
 * manageRunnitBoardAccessSettings enforces: MANAGE_ALL_GENERATIONS and nothing else (owning the
 * board is not enough), and a featured template only for a RunDiffusion admin. Only a board of the
 * team in hand, too, since the owner picker lists that team's members.
 *
 * Components ask TeamUserMixin's canChangeRunnitOwner(), which fills in the context.
 */
export function isRunnitOwnerChangeAllowed (runnit: Runnit | null, ctx: OwnerChangeContext): boolean {
	if (!runnit || !runnit.teamId || !ctx.teamId || runnit.teamId !== ctx.teamId) return false;
	if (runnit.type === RUNNIT_TYPE.TEMPLATE && runnit.isFeatured) return ctx.isAdmin;
	return ctx.hasManageAllGenerationsPermission;
}

/**
 * How the dialog names a board's owner: "You (email)" for the caller, the owner's email, or
 * "No owner" once they have left the team (the Boards table's word for it).
 */
export function ownerLabel (ownerUserId: string | null, ownerEmail: string | null, currentUserId: string | null): string {
	if (ownerUserId && ownerUserId === currentUserId) return ownerEmail ? `You (${ownerEmail})` : 'You';
	return ownerEmail || 'No owner';
}

/**
 * Whether a single board's save changes its access, the owner aside: its level, or who is on a
 * RESTRICTED board. Without such a change an owner change is sent alone, so it needs only the
 * owner permission and not the sharing ones.
 *
 * `addedTeamMembers` always holds the owner the dialog opened with (it loads them so they are
 * listed), so "added" means added and not already on the board.
 */
export function hasAccessChanges (args: {
	runnit: Runnit
	accessLevel: RunnitsAccessLevel
	runnitMembers: RunnitMember[]
	addedTeamMembers: TeamMember[]
	removedMemberIds: Record<string, true>
	ownerUserId: string | null
}): boolean {
	const { runnit, accessLevel, ownerUserId } = args;
	if (accessLevel !== (runnit.accessLevel || RUNNITS_ACCESS_LEVEL.PRIVATE)) return true;
	if (accessLevel !== RUNNITS_ACCESS_LEVEL.RESTRICTED) return false;
	if (Object.keys(args.removedMemberIds).some((userId: string) => userId !== ownerUserId)) return true;

	const onBoard: Record<string, true> = {};
	args.runnitMembers.forEach((member: RunnitMember) => {
		onBoard[member.userId || member.id] = true;
	});
	Object.keys(runnit.userIds || {}).forEach((userId: string) => {
		onBoard[userId] = true;
	});
	return args.addedTeamMembers.some((teamMember: TeamMember) => (
		!!teamMember.userId &&
		teamMember.userId !== ownerUserId &&
		!onBoard[teamMember.userId]
	));
}

/**
 * What a single board's save sends. The access part goes out on every save it always went out on,
 * which is every save except an owner change made alone.
 */
export function singleSavePayload (args: {
	runnitId: string
	accessLevel: RunnitsAccessLevel
	/** Set only when the owner changes. */
	newOwnerUserId: string | null
	accessChanged: boolean
	addedUserIds: string[]
	removedUserIds: string[]
}): ManageAccessPayload {
	const sendsAccess: boolean = !args.newOwnerUserId || args.accessChanged;
	const isRestricted: boolean = args.accessLevel === RUNNITS_ACCESS_LEVEL.RESTRICTED;
	return {
		runnitId: args.runnitId,
		...(args.newOwnerUserId ? { ownerUserId: args.newOwnerUserId } : {}),
		...(sendsAccess ? {
			accessLevel: args.accessLevel,
			addedUserIds: isRestricted ? args.addedUserIds : [],
			removedUserIds: isRestricted ? args.removedUserIds : [],
		} : {}),
	};
}

/**
 * Which selected boards a bulk save touches. Each board takes every requested change or none, so
 * none ends up half changed: a board whose owner the caller cannot change is skipped once an owner
 * is chosen, and one whose access they cannot manage once a level is. Before anything is chosen,
 * every board the caller can change either way.
 */
export function bulkEditableRunnits (runnits: Runnit[], args: {
	ownerRequested: boolean
	accessRequested: boolean
	canManageAccess: (runnit: Runnit) => boolean
	canChangeOwner: (runnit: Runnit) => boolean
}): Runnit[] {
	return runnits.filter((runnit: Runnit) => {
		if (!args.ownerRequested && !args.accessRequested) {
			return args.canManageAccess(runnit) || args.canChangeOwner(runnit);
		}
		return (
			(!args.ownerRequested || args.canChangeOwner(runnit)) &&
			(!args.accessRequested || args.canManageAccess(runnit))
		);
	});
}

/**
 * What one board's bulk save sends, or null when there is nothing to send: a board the chosen
 * owner already owns, with its access level kept.
 */
export function bulkSavePayload (runnit: Runnit, args: {
	newOwnerUserId: string | null
	accessLevel: AccessLevelChoice
	/** The member list a RESTRICTED save replaces each board's with. The owner is always kept. */
	setUserIds: string[]
}): ManageAccessPayload | null {
	const changesOwner: boolean = !!args.newOwnerUserId && args.newOwnerUserId !== runnit.userId;
	const changesAccess: boolean = args.accessLevel !== KEEP_ACCESS_LEVEL;
	if (!changesOwner && !changesAccess) return null;
	return {
		runnitId: runnit.id,
		...(changesOwner ? { ownerUserId: args.newOwnerUserId } : {}),
		...(changesAccess ? {
			accessLevel: args.accessLevel as RunnitsAccessLevel,
			...(args.accessLevel === RUNNITS_ACCESS_LEVEL.RESTRICTED ? { setUserIds: args.setUserIds } : {}),
		} : {}),
	};
}
